import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import Card from "../Base/Card";
import SuccessModal from "../SuccessModal";
import ErrorViewer from "../ErrorViewer";
import {getLGA, getStates} from "../../redux/utils/utilsSlice";
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {GetUserInfo, UpdateKYC} from "../../redux/selfService/selfServiceSlice";
import LoadingSpinner from "../LoadingSpinner";
import EmployerSelect from "../EmployerSelect";

interface KYCPropsI {
    isOnboarding?: boolean;
    onSuccess: ()=>void;
}

const ChangePassword = ({ isOnboarding, onSuccess }: KYCPropsI)=>{
    const dispatch = useAppDispatch();
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const { allStates, lgas } = useAppSelector((state) => state?.utils);
    const { user } = useAppSelector((state) => state?.auth);
    const { userInfo, isLoading } = useAppSelector((state) => state?.selfService);


    useEffect(()=>{
       // Define an asynchronous function to fetch user info and states
    const fetchData = async () => {
        await dispatch(GetUserInfo());
        await dispatch(getStates());
        // Once both actions are complete, call onStateCodeChanged
        onStateCodeChanged(userInfo.stateCode);
      };
  
      // Call the fetchData function
      fetchData();
    }, [])

    useEffect(()=>{
        reset({
            firstname: userInfo?.firstname,
            lastname: userInfo?.surname,
            othername: userInfo?.othername,
            email: userInfo?.email,
            phoneNumber: userInfo?.mobileNumber,
            house_plot_number: userInfo?.house_Plot_No,
            streetName: userInfo?.streetName,
            city: userInfo?.city,
            stateCode: userInfo?.stateCode,
            localGovernment: userInfo?.localGovernment,
        })
    }, [userInfo])

    const onStateCodeChanged = (stateCode: string)=>{
        dispatch(getLGA({ stateCode }))
    }

    const validationSchema = Yup.object().shape({
        firstname: Yup.string().required("Firstname is required"),
        lastname: Yup.string().required("Lastname is required"),
        othername: Yup.string(),
        email: Yup.string().required("Email is required").email("Not a valid email"),
        phoneNumber: Yup.string().required("Phone number is required"),
        house_plot_number: Yup.string().required("House plot number is required"),
        streetName: Yup.string().required("Street name is required"),
        city: Yup.string().required("City is required"),
        stateCode: Yup.string().required("State code is required"),
        localGovernment: Yup.string().required("Local government is required"),
    });

    const { handleSubmit, register, setValue, reset, formState: { errors },} = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            firstname: userInfo?.firstname,
            lastname: userInfo?.surname,
            othername: userInfo?.othername,
            email: userInfo?.email,
            phoneNumber: userInfo?.mobileNumber,
            house_plot_number: userInfo?.house_Plot_No,
            streetName: userInfo?.streetName,
            city: userInfo?.city,
            stateCode: userInfo?.stateCode,
            localGovernment: userInfo?.localGovernment,
        }
    });

    const onSubmit = async (values: any)=>{
        const updateKYCAction = await dispatch(UpdateKYC(values));
        if(updateKYCAction?.type==="selfService/UpdateKYC/fulfilled" && updateKYCAction.payload.statusCode === "00"){
            setShowSuccessModal(true)
            onSuccess();
        }
    }

    const toggleShowSuccessModal = () => setShowSuccessModal(!showSuccessModal)

   
    return (


        <Card  className="w-full">
             {/* className={"relative"} */}
            <SuccessModal
                showModal={showSuccessModal}
                onClose={toggleShowSuccessModal}
                note={"Your KYC Information has been updated successfully."}
            />
            {/*<div className="load">*/}
            {/*    <LoadingSpinner size="lg" />*/}
            {/*</div>*/}
            <>
                {!isOnboarding && <div className="form__head mb-8 text-center">
                    Update KYC Information
                </div>}
                <div className="form__row">
                    <div className="form__group">
                        <label htmlFor="firstname" className="form__label">
                            Firstname
                        </label>
                        <input
                            id="firstname"
                            placeholder="Firstname"
                            className="form__input"
                            {...register("firstname")}
                        />
                        {errors.firstname && (
                            <ErrorViewer message={errors.firstname.message as string}/>
                        )}
                    </div>
                    <div className="form__group">
                        <label htmlFor="lastname" className="form__label">
                            Lastname
                        </label>
                        <input
                            id="lastname"
                            placeholder="Lastname"
                            className="form__input"
                            {...register("lastname")}
                        />
                        {errors.lastname && (
                            <ErrorViewer message={errors.lastname.message as string}/>
                        )}
                    </div>
                </div>
                <div className="form__row">
                    <div className="form__group">
                        <label htmlFor="other-names" className="form__label">
                            Other Names
                        </label>
                        <input
                            id="other-names"
                            placeholder="Other Names"
                            className="form__input"
                            {...register("othername")}
                        />
                    </div>
                    <div className="form__group">
                        <label htmlFor="email" className="form__label">
                            Email
                        </label>
                        <input
                            id="email"
                            placeholder="Email"
                            className="form__input"
                            type="email"
                            {...register("email")}
                        />
                        {errors.email && (
                            <ErrorViewer message={errors.email.message as string}/>
                        )}
                    </div>
                </div>
                <div className="form__row">
                    <div className="form__group">
                        <label htmlFor="phone-number" className="form__label">
                            Phone Number
                        </label>
                        <input
                            id="phone-number"
                            placeholder="Phone Number"
                            className="form__input"
                            {...register("phoneNumber")}
                        />
                        {errors.phoneNumber && (
                            <ErrorViewer message={errors.phoneNumber.message as string}/>
                        )}
                    </div>
                    <div className="form__group">
                        <label htmlFor="house-plot-number" className="form__label">
                            House Plot Number
                        </label>
                        <input
                            id="house-plot-number"
                            placeholder="House Plot Number"
                            className="form__input"
                            {...register("house_plot_number")}
                        />
                        {errors.house_plot_number && (
                            <ErrorViewer message={errors.house_plot_number.message as string}/>
                        )}
                    </div>
                </div>
                <div className="form__row">
                    <div className="form__group">
                        <label htmlFor="street-name" className="form__label">
                            Street Name
                        </label>
                        <input
                            id="street-name"
                            placeholder="Street Name"
                            className="form__input"
                            {...register("streetName")}
                        />
                        {errors.streetName && (
                            <ErrorViewer message={errors.streetName.message as string}/>
                        )}
                    </div>
                    <div className="form__group">
                        <label htmlFor="city" className="form__label">
                            City
                        </label>
                        <input
                            id="city"
                            placeholder="City"
                            className="form__input"
                            {...register("city")}
                        />
                        {errors.city && (
                            <ErrorViewer message={errors.city.message as string}/>
                        )}
                    </div>
                </div>
                <div className="form__row">
                    <div className="form__group">
                        <label htmlFor="state" className="form__label">
                            State
                        </label>
                        
                        
                        <select
    id="state"
    className="form__select"
    onChange={(e) => {
        onStateCodeChanged(e.target.value);
        setValue('stateCode', e.target.value);
        setValue('localGovernment', '');
    }}
    defaultValue={userInfo?.stateCode} // Set the default value based on user info
>
    <option value="" disabled>Select State</option>
    {allStates && Object.keys(allStates).map((a) => (
        <option value={a} key={a}>
            {allStates[a]}
        </option>
    ))}
</select>

                        {errors.stateCode && (
                            <ErrorViewer message={errors.stateCode.message as string}/>
                        )}
                    </div>
                    <div className="form__group">
                        <label htmlFor="lga" className="form__label">
                            LGA
                        </label>
                        <select
    id="localGovernment"
    defaultValue={userInfo?.localGovernment || ""} // Set the default value based on user info
    className="form__select"
    {...register('localGovernment')}
>
    <option value="" disabled>Select LGA</option>
    {lgas && Object.keys(lgas).map((lgaKey) => (
        <option value={lgaKey} key={lgaKey}>
            {lgas[lgaKey]}
        </option>
    ))}
</select>

                        {errors.localGovernment && (
                            <ErrorViewer message={errors.localGovernment.message as string}/>
                        )}
                    </div>
                </div>






                <div className="mt-4 text-center">
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                    >
                        {
                            isLoading ? <LoadingSpinner /> :
                            "Confirm"
                        }
                    </button>
                </div>
            </>
        </Card>
    )
}
export default ChangePassword;